import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Collapsible from '@radix-ui/react-collapsible';
import ChooseMediaModal from '@/components/media/ChooseMediaModal';
import StoryMediaThumb from '@/components/memories/StoryMediaThumb';
import CollapsibleChevron from '@/components/shared/CollapsibleChevron';
import Close from '@/icons/Close';
import Plus from '@/icons/Plus';
import { STORY_CONTENT_MAX_LENGTH, STORY_TITLE_MAX_LENGTH } from '@/types/story';

import './story-form.css';

interface FieldCharCountProps {
  id: string;
  value: string;
  maxLength: number;
}

function FieldCharCount({ id, value, maxLength }: FieldCharCountProps) {
  const { t } = useTranslation('forms');
  const remaining = maxLength - value.length;
  const atLimit = remaining <= 0;
  const nearLimit = remaining > 0 && remaining <= Math.min(50, Math.ceil(maxLength * 0.1));

  return (
    <p
      id={id}
      className={`story-form__char-count${atLimit ? ' story-form__char-count--at-limit' : ''}${nearLimit ? ' story-form__char-count--near-limit' : ''}`}
      aria-live="polite"
    >
      {t('story.charsRemaining', { count: remaining })}
    </p>
  );
}

export interface StoryFormValues {
  title: string;
  content: string;
  mediaAssetIds: string[];
}

export type StoryFormAttachmentMeta = {
  mimeType?: string;
  filename?: string;
};

/** Initial form state; `mediaMeta` is display-only (video/audio thumbs) and not submitted. */
export type StoryFormInitialValues = StoryFormValues & {
  mediaMeta?: Record<string, StoryFormAttachmentMeta>;
};

interface StoryFormProps {
  familyId: string;
  familyMemberId: string;
  initialValues?: StoryFormInitialValues;
  isEditing?: boolean;
  isSaving?: boolean;
  errorMessage?: string | null;
  /** Shown under the error when create/update hit a paid plan limit. */
  upgradeHint?: string | null;
  onCancel: () => void;
  onSubmit: (values: StoryFormValues) => void | Promise<void>;
}

const StoryForm: React.FC<StoryFormProps> = ({
  familyId,
  familyMemberId,
  initialValues,
  isEditing = false,
  isSaving = false,
  errorMessage,
  upgradeHint = null,
  onCancel,
  onSubmit
}) => {
  const { t } = useTranslation('forms');
  const { t: tCommon } = useTranslation('common');
  const [title, setTitle] = useState(initialValues?.title ?? '');
  const [content, setContent] = useState(initialValues?.content ?? '');
  const [mediaAssetIds, setMediaAssetIds] = useState<string[]>(initialValues?.mediaAssetIds ?? []);
  const [mediaMeta, setMediaMeta] = useState<Record<string, StoryFormAttachmentMeta>>(() => {
    if (initialValues?.mediaMeta) return { ...initialValues.mediaMeta };
    return (initialValues?.mediaAssetIds ?? []).reduce<Record<string, StoryFormAttachmentMeta>>((acc, id) => {
      acc[id] = {};
      return acc;
    }, {});
  });
  const [showMediaModal, setShowMediaModal] = useState(false);
  const [tipsOpen, setTipsOpen] = useState(isEditing);

  const handleAddMedia = (mediaAssetId: string, meta?: { mimeType?: string; originalFilename?: string }) => {
    if (mediaAssetIds.includes(mediaAssetId)) return;
    setMediaAssetIds((prev) => [...prev, mediaAssetId]);
    setMediaMeta((prev) => ({
      ...prev,
      [mediaAssetId]: {
        mimeType: meta?.mimeType ?? prev[mediaAssetId]?.mimeType,
        filename: meta?.originalFilename ?? prev[mediaAssetId]?.filename
      }
    }));
    setShowMediaModal(false);
  };

  const handleRemoveMedia = (mediaAssetId: string) => {
    setMediaAssetIds((prev) => prev.filter((id) => id !== mediaAssetId));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSubmit({ title, content, mediaAssetIds });
  };

  const submitLabel = isEditing ? tCommon('save') : t('story.publishMemory');
  const footerCopy = t('story.visibilityFooter');
  const titleValid = title.trim().length > 0 && title.length <= STORY_TITLE_MAX_LENGTH;
  const contentValid = content.trim().length > 0 && content.length <= STORY_CONTENT_MAX_LENGTH;
  const canSubmit = titleValid && contentValid && !isSaving;

  return (
    <form className="story-form" onSubmit={handleSubmit}>
      {errorMessage && (
        <div className="story-form__error" role="alert">
          <p className="story-form__error-message">{errorMessage}</p>
          {upgradeHint ? <p className="story-form__upgrade-hint">{upgradeHint}</p> : null}
        </div>
      )}

      <div className="story-form__field">
        <label className="story-form__label" htmlFor="story-title">
          {t('story.title')}
        </label>
        <input
          id="story-title"
          type="text"
          className="story-form__input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={STORY_TITLE_MAX_LENGTH}
          aria-describedby="story-title-char-count"
          required
        />
        <FieldCharCount id="story-title-char-count" value={title} maxLength={STORY_TITLE_MAX_LENGTH} />
      </div>

      <div className="story-form__field">
        <label className="story-form__label" htmlFor="story-content">
          {t('story.memory')}
        </label>
        <textarea
          id="story-content"
          className="story-form__textarea"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          maxLength={STORY_CONTENT_MAX_LENGTH}
          aria-describedby="story-content-char-count story-content-hint"
          rows={8}
          required
        />
        <FieldCharCount id="story-content-char-count" value={content} maxLength={STORY_CONTENT_MAX_LENGTH} />
        <p id="story-content-hint" className="story-form__field-hint">
          {t('story.contentHint')}
        </p>
      </div>

      <Collapsible.Root open={tipsOpen} onOpenChange={setTipsOpen} className="story-form__tips">
        <Collapsible.Trigger asChild>
          <button type="button" className="story-form__tips-trigger">
            <CollapsibleChevron className="story-form__tips-chevron" />
            {t('story.writingTips')}
          </button>
        </Collapsible.Trigger>
        <Collapsible.Content className="story-form__tips-body">
          <p>{t('story.writingTipsIntro')}</p>
          <ul>
            <li>{t('story.writingTipPhrase')}</li>
            <li>{t('story.writingTipSensory')}</li>
            <li>{t('story.writingTipHabit')}</li>
            <li>{t('story.writingTipScene')}</li>
          </ul>
        </Collapsible.Content>
      </Collapsible.Root>

      <div className="story-form__field">
        <div className="story-form__attachments-head">
          <label className="story-form__label">{t('story.attachments')}</label>
          <button type="button" className="btn btn--ghost btn--sm" onClick={() => setShowMediaModal(true)}>
            <Plus width={13} height={13} />
            {tCommon('add')}
          </button>
        </div>
        {mediaAssetIds.length > 0 && (
          <div className="story-form__attachments">
            {mediaAssetIds.map((assetId) => (
              <div key={assetId} className="story-form__attachment">
                <StoryMediaThumb
                  familyId={familyId}
                  mediaAssetId={assetId}
                  mimeType={mediaMeta[assetId]?.mimeType}
                  size="40x40"
                />
                <button
                  type="button"
                  className="story-form__attachment-remove card-hover-remove"
                  title={t('story.removeAttachment')}
                  aria-label={t('story.removeAttachment')}
                  onClick={() => handleRemoveMedia(assetId)}
                >
                  <Close width={15} height={15} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <p className="story-form__footer-copy">{footerCopy}</p>

      <div className="story-form__actions">
        <button type="button" className="btn btn--ghost" onClick={onCancel} disabled={isSaving}>
          {tCommon('cancel')}
        </button>
        <button type="submit" className="btn btn--primary" disabled={!canSubmit}>
          {isSaving ? tCommon('saving') : submitLabel}
        </button>
      </div>

      <ChooseMediaModal
        title={t('story.addAttachment')}
        familyId={familyId}
        familyMemberId={familyMemberId}
        acceptKinds="all"
        setAsPrimaryProfilePhoto={false}
        excludeMediaAssetIds={mediaAssetIds}
        isOpen={showMediaModal}
        onClose={() => setShowMediaModal(false)}
        onSelect={handleAddMedia}
      />
    </form>
  );
};

export default StoryForm;
